import Button from 'components/Button';
import Error from 'components/form/Error';
import ActionModal from 'components/Modal/ActionModal';
import Space from 'components/Space';
import { FC, useContext, useState } from 'react';
import { mutate } from 'swr';
import Interview from 'types/Interview';
import privateAPI from 'utils/privateAxiosInstance';
import { getLocaleDate, getLocaleTime } from 'utils/time';
import { ReviewPageContext } from '.';
import { ButtonsContainer, Container } from './components';

const Accepted: FC = () => {
  const { interview } = useContext(ReviewPageContext);
  const dateDifference = interview?.date
    ? new Date().getTime() - new Date(interview?.date).getTime()
    : undefined;
  return dateDifference && dateDifference < 0 ? (
    <AcceptedBeforeDate />
  ) : (
    <AcceptedAfterDate />
  );
};

const AcceptedBeforeDate: FC = () => {
  const { interview } = useContext(ReviewPageContext);
  const isAuthorMentor = interview?.authorId === interview?.mentorship?.mentorId;
  const [cancelOpen, setCancelOpen] = useState<boolean>(false);
  const [cancelError, setCancelError] = useState<boolean>(false);
  const [cancelLoading, setCancelLoading] = useState<boolean>(false);

  const handleCancel = () => {
    setCancelLoading(true);
    setCancelError(false);
    privateAPI
      .post(`/interview/mentor-cancel/${interview?.id}`)
      .then(() => {
        mutate(`/interview/details/mentor/${interview?.id}`, {
          ...interview,
          status: 'CANCELLED',
        } as Interview);
        mutate(`/interview/mentor`);
      })
      .catch(() => {
        setCancelError(true);
      })
      .finally(() => {
        setCancelLoading(false);
        setCancelOpen(false);
      });
  };
  return (
    <>
      <ActionModal
        open={cancelOpen}
        close={() => setCancelOpen(false)}
        title="Annuler l'entretien"
        applyText="Annuler l'entretien"
        apply={handleCancel}
        cancelText="Retour"
        color="error"
        loading={cancelLoading}
      >
        <p>
          Vous êtes sur le point d'annuler l'entretien prévu à la date du{' '}
          {interview?.date && getLocaleDate(interview.date)} à{' '}
          {interview?.date && getLocaleTime(interview.date)}.
        </p>
        <Space height={1} />
        <p>
          Cette action est irréversible. Souhaitez-vous vraiment annuler cet
          entretien ?
        </p>
      </ActionModal>
      <Container>
        <p>
          {isAuthorMentor 
            ? `${interview?.mentorship?.mentee?.firstName} a accepté l'entretien du `
            : `Vous avez accepté la proposition d'entretien de ${interview?.mentorship?.mentee?.firstName} pour le `
          }
          {interview?.date && getLocaleDate(interview.date)} à{' '}
          {interview?.date && getLocaleTime(interview.date)}.
        </p>
        <p>
          Pensez à noter ce rendez-vous dans votre agenda pour ne pas le
          manquer.
        </p>
        <Space height={1} />
        <p>
          Si vous veniez à avoir un imprévu, il vous est toujours possible
          d'annuler l'entretien à l'aide du bouton ci-dessous.
        </p>
        <ButtonsContainer>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => setCancelOpen(true)}
          >
            Annuler l'entretien
          </Button>
        </ButtonsContainer>
        {cancelError && (
          <ButtonsContainer>
            <Error>Une erreur est survenue.</Error>
          </ButtonsContainer>
        )}

        <Space height={3} />
        <p>
          Vous pouvez également rejoindre votre salon de visio-conférence à
          l'aide du bouton ci-dessous.
        </p>
        <ButtonsContainer>
          <a
            href={`https://meet.jit.si/mentorat-sextant-n-${interview?.mentorship?.mentorId}-${interview?.mentorship?.menteeId}-${interview?.id}`}
            target="_blank"
            rel="noreferrer"
          >
            <Button color="primary" variant="outlined">
              Rejoindre la visio-conférence
            </Button>
          </a>
        </ButtonsContainer>
      </Container>
    </>
  );
};

const AcceptedAfterDate: FC = () => {
  const { interview } = useContext(ReviewPageContext);

  const [finishLoading, setFinishLoading] = useState<boolean>(false);
  const [finishError, setFinishError] = useState<boolean>(false);
  const handleFinish = () => {
    setFinishLoading(true);
    setFinishError(false);

    privateAPI
      .post(`/interview/mentor-finish/${interview?.id}`)
      .then(() => {
        mutate(`/interview/details/mentor/${interview?.id}`, {
          ...interview,
          status: 'WAITING_REVIEW',
        } as Interview);
        mutate(`/interview/mentor`);
      })
      .catch(() => {
        setFinishError(true);
      })
      .finally(() => {
        setFinishLoading(false);
      });
  };

  const [lateOpen, setLateOpen] = useState<boolean>(false);
  const [lateLoading, setLateLoading] = useState<boolean>(false);
  const [lateError, setLateError] = useState<boolean>(false);

  const handleSignalLate = () => {
    setLateError(false);
    setLateLoading(true);
    privateAPI
      .post(`/interview/mentor-signal-late/${interview?.id}`)
      .then(() => {
        mutate(`/interview/details/mentor/${interview?.id}`, {
          ...interview,
          status: 'LATE',
          menteeLate: true,
        } as Interview);
        mutate(`/interview/mentor`);
      })
      .catch(() => {
        setLateError(true);
      })
      .finally(() => {
        setLateLoading(false);
        setLateOpen(false);
      });
  };
  const [selfLateOpen, setSelfLateOpen] = useState<boolean>(false);
  const [selfLateLoading, setSelfLateLoading] = useState<boolean>(false);

  const handleSignalSelfLate = () => {
    setLateError(false);
    setSelfLateLoading(true);
    privateAPI
      .post(`/interview/mentor-signal-self-late/${interview?.id}`)
      .then(() => {
        mutate(`/interview/details/mentor/${interview?.id}`, {
          ...interview,
          status: 'LATE',
          mentorLate: true,
        } as Interview);
        mutate(`/interview/mentor`);
      })
      .catch(() => {
        setLateError(true);
      })
      .finally(() => {
        setSelfLateLoading(false);
        setSelfLateOpen(false);
      });
  };

  return (
    <>
      <ActionModal
        title="Signaler un retard"
        color="error"
        open={lateOpen}
        close={() => {
          setLateOpen(false);
        }}
        apply={handleSignalLate}
        applyText="Signaler"
        loading={lateLoading}
      >
        <p>
          Vous êtes sur le point de signaler un retard de la part de votre
          mentoré.
        </p>
        <p>Cette action est irreversible. Confirmez-vous ce retard ?</p>
      </ActionModal>
      <ActionModal
        title="Signaler un retard"
        color="error"
        open={selfLateOpen}
        close={() => {
          setSelfLateOpen(false);
        }}
        apply={handleSignalSelfLate}
        applyText="Signaler"
        loading={selfLateLoading}
      >
        <p>
          Vous êtes sur le point de signaler que vous n'avez pas pu vous
          présenter à cet entretien.
        </p>
        <p>Cette action est irreversible.</p>
      </ActionModal>
      <Container>
        <p>
          Votre entretien avec {interview?.mentorship?.mentee?.firstName} a déjà
          commencé ou va bientôt avoir lieu.
        </p>
        <p>
          Si vous souhaitez le réaliser en visio-conférence, vous pouvez
          rejoindre votre salon privé en cliquant sur le bouton ci-dessous.
        </p>
        <ButtonsContainer>
          <a
            href={`https://meet.jit.si/mentorat-sextant-n-${interview?.mentorship?.mentorId}-${interview?.mentorship?.menteeId}-${interview?.id}`}
            target="_blank"
            rel="noreferrer"
          >
            <Button color="primary" variant="outlined">
              Rejoindre la visio-conférence
            </Button>
          </a>
        </ButtonsContainer>
        <Space height={3} />
        <p>
          Une fois votre entretien terminé, que vous l'ayez réalisé à l'aide de
          la visio-conférence ou d'une autre manière, vous pouvez l'indiquer en
          cliquant sur le bouton ci-dessous.
        </p>
        <p>
          Vous et {interview?.mentorship?.mentee?.firstName} devrez alors
          remplir des fiches de compte-rendu, qui vous serviront à garder une
          trace de son évolution au fur et à mesure de vos entretiens.
        </p>
        <ButtonsContainer>
          <Button
            color="primary"
            onClick={handleFinish}
            loading={finishLoading}
          >
            L'entretien est terminé
          </Button>
        </ButtonsContainer>
        {finishError && (
          <ButtonsContainer>
            <Error>Une erreur est survenue.</Error>
          </ButtonsContainer>
        )}
        <Space height={3} />
        <p>
          Si {interview?.mentorship?.mentee?.firstName} ne s'est pas présenté(e)
          lors de l'entretien, vous pouvez le signaler à l'aide du bouton situé
          ci-dessous.
        </p>
        <ButtonsContainer>
          <Button
            color="error"
            variant="outlined"
            onClick={() => setLateOpen(true)}
          >
            {interview?.mentorship?.mentee?.firstName} ne s'est pas présenté(e)
          </Button>
        </ButtonsContainer>
        <ButtonsContainer>
          <Button
            color="error"
            variant="outlined"
            onClick={() => setSelfLateOpen(true)}
          >
            Je n'ai pas pu participer à l'entretien
          </Button>
        </ButtonsContainer>
        {lateError && (
          <ButtonsContainer>
            <Error>Une erreur est survenue.</Error>
          </ButtonsContainer>
        )}
      </Container>
    </>
  );
};

export default Accepted;
